import {
    keepPreviousData,
    useQuery,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query'
import { ReactNode } from 'react'
import { Loading } from 'vnetwork-ui'
import ErrorPage from '@/components/ErrorBoundary/ErrorPage'
import { AxiosError } from 'axios'
import { QueryKey as ValidQueryKey } from '@/constants/reactQueryKeys'
import { SkeletonTable } from '@/components/ui/skeleton'
import QueryErrorAlert from '@/components/ErrorBoundary/QueryErrorAlert'

type ErrorDisplayMode = 'alert' | 'page'

/**
 * Valid query key types - must start with a key from QUERY_KEYS constants
 * Allows additional parameters after the base key for dynamic queries
 *
 * @example [QUERY_KEYS.SERVICES, serviceId]
 * @example [QUERY_KEYS.CUSTOMERS, 'detail', customerId]
 */
type ValidQueryKeyArray = readonly [ValidQueryKey, ...any[]]

/**
 * Extract HTTP status code from error object
 */
const getErrorStatusCode = (error: unknown): number => {
    // Check if it's an AxiosError with response
    if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as AxiosError
        return axiosError.response?.status ?? 500
    }

    // Check if error has status property
    if (error && typeof error === 'object' && 'status' in error) {
        const statusError = error as { status: number }
        return statusError.status
    }

    // Default to 500 for unknown errors
    return 500
}

interface QueryHandlerOptions<TError = Error> {
    /**
     * Custom loading component to render
     * @example loadingComponent: <Loading.CircularProgress width={40} height={40} />
     */
    loadingComponent?: ReactNode
    /**
     * Error display mode: 'alert' shows inline alert, 'page' shows full error page
     * @default 'alert'
     */
    errorDisplayMode?: ErrorDisplayMode
    /**
     * Custom error component to render when query has error (has highest priority)
     */
    renderError?: (error: TError) => ReactNode
    /**
     * Whether to show loading state (default: true)
     */
    showLoading?: boolean
    /**
     * Whether to show error state (default: true)
     */
    showError?: boolean
}

type UseQueryWithHandlerResult<
    TData = unknown,
    TError = Error,
> = UseQueryResult<TData, TError> & {
    /**
     * Returns the state component (loading/error) if applicable, otherwise null
     * Use this to render loading/error states in your component
     */
    renderState: () => ReactNode
}

/**
 * Custom hook wrapper around useQuery that provides standardized error and loading handling
 *
 * IMPORTANT: queryKey must start with a key from QUERY_KEYS constants for type safety
 *
 * @example
 * ```tsx
 * import { QUERY_KEYS } from '@/constants/reactQueryKeys'
 *
 * const { data: service, renderState } = useQueryWithHandler({
 *   queryFn: () => getService(id),
 *   queryKey: [QUERY_KEYS.SERVICES, id], // ✅ Type-safe
 * })
 *
 * const stateComponent = renderState()
 * if (stateComponent) return stateComponent
 *
 * return <YourContent data={service} />
 * ```
 *
 * @example With custom loading component
 * ```tsx
 * const { data, renderState } = useQueryWithHandler(
 *   {
 *     queryFn: () => fetchData(),
 *     queryKey: [QUERY_KEYS.CUSTOMERS],
 *   },
 *   {
 *     loadingComponent: <Loading.CircularProgress width={40} height={40} />,
 *   }
 * )
 * ```
 *
 * @example With error page mode (full page error instead of inline alert)
 * ```tsx
 * const { data, renderState } = useQueryWithHandler(
 *   {
 *     queryFn: () => fetchData(),
 *     queryKey: [QUERY_KEYS.INVOICES, 'waiting'],
 *   },
 *   {
 *     errorDisplayMode: 'page', // Shows ErrorPage component
 *   }
 * )
 * ```
 *
 * @example With custom error renderer (highest priority)
 * ```tsx
 * const { data, renderState } = useQueryWithHandler(
 *   {
 *     queryFn: () => fetchData(),
 *     queryKey: [QUERY_KEYS.CONTRACTS, contractId],
 *   },
 *   {
 *     renderError: (error) => <CustomErrorBanner error={error} />,
 *   }
 * )
 * ```
 */
export const useQueryWithHandler = <TData = unknown, TError = Error>(
    options: Omit<
        UseQueryOptions<TData, TError>,
        'queryKey' | 'placeholderData'
    > & {
        queryKey: ValidQueryKeyArray
        loadingComponent?: ReactNode
        /**
         * Keep previous data while fetching new data
         * Pass `true` as shorthand for `keepPreviousData`
         * @example placeholderData: true // Keeps previous data during refetch
         */
        placeholderData?:
            | true
            | UseQueryOptions<TData, TError>['placeholderData']
    },
    handlers?: QueryHandlerOptions<TError>
): UseQueryWithHandlerResult<TData, TError> => {
    // Transform placeholderData: true to keepPreviousData
    const { placeholderData, ...restOptions } = options
    const resolvedPlaceholderData =
        placeholderData === true ? keepPreviousData : placeholderData

    const query = useQuery<TData, TError>({
        ...restOptions,
        placeholderData: resolvedPlaceholderData,
    } as UseQueryOptions<TData, TError>)

    const renderState = (): ReactNode => {
        // Handle loading state
        if (query.isLoading && handlers?.showLoading !== false) {
            // If placeholderData is true, use SkeletonTable for better UX with data tables
            if (placeholderData === true && !handlers?.loadingComponent) {
                return <SkeletonTable rows={10} columns={6} />
            }
            return handlers?.loadingComponent ?? <Loading.FadingDots />
        }

        // Handle error state
        if (query.isError && handlers?.showError !== false) {
            // Priority: renderError > errorDisplayMode > default (alert)
            if (handlers?.renderError) {
                return handlers.renderError(query.error as TError)
            }

            const errorDisplayMode = handlers?.errorDisplayMode ?? 'alert'
            if (errorDisplayMode === 'page') {
                const statusCode = getErrorStatusCode(query.error)
                return (
                    <ErrorPage
                        status={statusCode}
                        error={query.error as Error}
                    />
                )
            }

            return (
                <QueryErrorAlert
                    error={query.error}
                    onRetry={() => query.refetch()}
                />
            )
        }

        return null
    }

    return {
        ...query,
        renderState,
    }
}
